import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import api, { formatApiErrorDetail } from "@/lib/api";

const TIERS = [
  {
    id: "starter",
    name: "Starter",
    price: { monthly: "$99", annual: "$990" },
    desc: "Focused monitoring of a single coastline or crop belt.",
    features: [
      "1 monitoring region",
      "Ocean OR Agriculture layer",
      "Daily satellite refresh",
      "Email anomaly alerts",
      "Standard support",
    ],
    featured: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: { monthly: "$299", annual: "$2,990" },
    desc: "Full ocean + agriculture intelligence with alerts and API.",
    features: [
      "Up to 10 monitoring regions",
      "Ocean + Agriculture layers",
      "Sub-30 min priority revisit",
      "Real-time alerts + API access",
      "Dashboard & data export",
      "Priority support",
    ],
    featured: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: { monthly: "Custom", annual: "Custom" },
    desc: "Dedicated tasking, on-prem options and SLAs for agencies.",
    features: [
      "Unlimited regions & users",
      "Dedicated satellite tasking",
      "Custom AI models & SLAs",
      "On-prem / VPC deployment",
      "Named solutions engineer",
    ],
    featured: false,
    contact: true,
  },
];

export default function Pricing() {
  const [loading, setLoading] = useState(null);
  const [billing, setBilling] = useState("monthly");

  const choose = async (tier) => {
    if (tier.contact) {
      window.location.hash = "#demo";
      return;
    }
    setLoading(tier.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: tier.id,
        billing_cycle: billing,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
      setLoading(null);
    }
  };

  return (
    <section id="pricing" className="relative bg-arjuna-ink py-24 sm:py-32" data-testid="pricing-section">
      <div className="container-x">
        <div className="max-w-2xl">
          <Reveal>
            <p className="overline">06. Pricing</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
              Plans that scale with your mission.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base leading-relaxed text-slate-400">
              Start small on a single region, or deploy planet-scale intelligence across
              your entire operation. All plans include the live map and anomaly feed.
            </p>
          </Reveal>
        </div>

        {/* Billing toggle */}
        <Reveal delay={0.25}>
          <div className="mt-10 inline-flex items-center gap-1 rounded-full border border-white/10 bg-arjuna-surface/50 p-1" data-testid="billing-toggle">
            <button
              data-testid="billing-monthly"
              onClick={() => setBilling("monthly")}
              className={`rounded-full px-5 py-2 text-sm transition-colors duration-200 ${
                billing === "monthly" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-annual"
              onClick={() => setBilling("annual")}
              className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm transition-colors duration-200 ${
                billing === "annual" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              Annual
              <span className="rounded-full bg-arjuna-agri/20 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-arjuna-agri">
                2 months free
              </span>
            </button>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {TIERS.map((tier, i) => (
            <Reveal key={tier.id} delay={i * 0.1}>
              <div
                data-testid={`plan-card-${tier.id}`}
                className={`relative flex h-full flex-col rounded-2xl border p-8 transition-transform duration-300 hover:-translate-y-1 ${
                  tier.featured
                    ? "border-arjuna-teal/50 bg-arjuna-surface glow-teal"
                    : "border-white/10 bg-arjuna-surface/40"
                }`}
              >
                {tier.featured && (
                  <span className="absolute -top-3 left-8 rounded-full bg-arjuna-teal px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-arjuna-ink">
                    Most popular
                  </span>
                )}
                <h3 className="font-heading text-xl text-white">{tier.name}</h3>
                <p className="mt-2 text-sm text-slate-400">{tier.desc}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="font-heading text-4xl font-semibold text-white" data-testid={`plan-price-${tier.id}`}>
                    {tier.price[billing]}
                  </span>
                  {!tier.contact && (
                    <span className="mb-1 text-sm text-slate-400">{billing === "annual" ? "/yr" : "/mo"}</span>
                  )}
                </div>
                {!tier.contact && billing === "annual" && (
                  <p className="mt-1 font-mono text-[11px] text-arjuna-agri">Save 2 months vs monthly</p>
                )}

                <ul className="mt-8 flex-1 space-y-3">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-arjuna-teal" />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`plan-cta-${tier.id}`}
                  onClick={() => choose(tier)}
                  disabled={loading === tier.id}
                  className={`mt-8 w-full ${tier.featured ? "btn-primary" : "btn-ghost"}`}
                >
                  {loading === tier.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      {tier.contact ? "Contact sales" : `Choose ${tier.name}`}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-center font-mono text-xs text-slate-500">
          Secure checkout by Stripe · Test mode · Cancel anytime
        </p>
      </div>
    </section>
  );
}
